import * as joint from "jointjs/dist/joint";
import * as R from "ramda";
import { nanoid } from "nanoid";
import ConnectLink from "@/plugins/joint/Link";
import textWidth from "@/utils/textWidth";
import { reactive, watch, ref } from "vue";

/**
 * @typedef {object} portsConfig
 * @property {number} in 输入口数量
 * @property {number} out 输出口数量
 * @property {number} maxOutLinkNumber 输出最大数量
 */

/**
 * @typedef {object} CommonRectangleCreateConfig
 * @property {object} data 初始化数据
 * @property {string} labelText 标签的文本
 * @property {object} size 图形大小
 * @property {number} size.width 图形长度
 * @property {number} size.height 图形高度
 * @property {portsConfig} ports 连接口
 * @property {string} outputType 输出类型
 */

const STROKE_WIDTH = 1;
const LABEL_PADDING_LEFT_RIGHT = 19;
const PORT_RADIUS = 4;

function elementData(data, model) {
  const graph = model.graph;
  const elementName = model.attributes.attrs.elementName.text;

  const getInData = () => {
    const inPorts = model.getGroupPorts("in");
    const inLinks = graph.getConnectedLinks(model, { inbound: true });
    const tmp = [];

    for (let i = 0; i < inPorts.length; i++) {
      const inPort = inPorts[i];
      let isUnused = false;

      for (let j = 0; j < inLinks.length; j++) {
        const inLink = inLinks[j];
        const target = inLink.target();
        if (target.port === inPort.id) {
          isUnused = true;
          const source = inLink.source();
          const tmpPortData = {
            type: "in",
            targetPortId: target.port,
            targetCellId: target.id,
            sourcePortId: source.port,
            sourceCellId: source.id,
            linkId: inLink.id,
          };
          tmp.push(tmpPortData);
        }
      }

      if (!isUnused) {
        const tmpPortData = {
          type: "in",
          targetPortId: inPort.id,
          targetCellId: model.id,
          sourcePortId: "",
          sourceCellId: "",
          linkId: "",
        };
        tmp.push(tmpPortData);
      }
    }

    return tmp;
  };

  const getOutData = () => {
    const outPorts = model.getGroupPorts("out");
    const outLinks = graph.getConnectedLinks(model, { outbound: true });
    const tmp = [];

    for (let i = 0; i < outPorts.length; i++) {
      const outPort = outPorts[i];

      for (let j = 0; j < outLinks.length; j++) {
        const outLink = outLinks[j];
        const source = outLink.source();
        if (source.port === outPort.id) {
          const target = outLink.target();
          const tmpPortData = {
            type: "out",
            targetPortId: target.port,
            targetCellId: target.id,
            sourcePortId: outPort.id,
            sourceCellId: model.id,
            linkId: outLink.id,
          };
          tmp.push(tmpPortData);
        }
      }
    }

    if (!tmp.length) {
      tmp.push({
        type: "out",
        targetPortId: "",
        targetCellId: "",
        sourcePortId: outPorts[0].id,
        sourceCellId: model.id,
        linkId: "",
      });
    }

    return tmp;
  };

  const removeOutLink = (outPortInfoItem) => {
    if (outPortInfoItem.linkId) {
      const link = graph.getCell(outPortInfoItem.linkId);
      link.remove();
    }
  };

  const removeInLink = (inPortInfoItem) => {
    if (inPortInfoItem.linkId) {
      const link = graph.getCell(inPortInfoItem.linkId);
      link.remove();
    }
  };

  const removeInPort = (portId) => {
    const inPort = model.getPort(portId);
    model.removePort(inPort);
  };

  const createOutData = () => {
    const outPorts = model.getGroupPorts("out");
    return {
      type: "out",
      targetPortId: "",
      targetCellId: "",
      sourcePortId: outPorts[0].id,
      sourceCellId: model.id,
      linkId: "",
    };
  };

  const createInData = () => {
    model.addInPort();
    const inPorts = model.getGroupPorts("in");
    return {
      type: "in",
      targetPortId: inPorts[inPorts.length - 1].id,
      targetCellId: model.id,
      sourcePortId: "",
      sourceCellId: "",
      linkId: "",
    };
  };

  const getCanConnectOutPortsList = () => {
    let cells = graph.getElements();
    const inLinks = graph.getConnectedLinks(model, { outbound: true });
    const connectedCellsObj = {};
    const tmp = [];

    inLinks.map((inLink) => {
      const source = inLink.source();
      connectedCellsObj[source.id] = {
        cellId: source.id,
        portId: source.port,
      };
    });

    cells = cells.filter((cell) => {
      if (cell.id === model.id) {
        return false;
      } else if (model.checkIsDownstreamCell({ model: cell })) {
        return false;
      } else if (model.checkIsParent(cell)) {
        return true;
      } else {
        return true;
      }
    });

    cells.map((cell) => {
      const outPorts = cell.getGroupPorts("out");
      const item = {
        cellId: cell.id,
        portId: outPorts[0].id,
        name: cell.attr("elementName/text"),
      };
      tmp.push(item);
    });

    return tmp;
  };

  const getCanConnectInPortsList = () => {
    let cells = graph.getElements();
    const outLinks = graph.getConnectedLinks(model, { outbound: true });
    const connectedCellsObj = {};
    const tmp = [];

    outLinks.map((outLink) => {
      const target = outLink.target();
      connectedCellsObj[target.id] = {
        cellId: target.id,
        portId: target.port,
      };
    });

    // 过滤当前cell以及上游cell
    cells = cells.filter((cell) => {
      if (cell.id === model.id) {
        return false;
      } else if (cell.checkIsDownstreamCell({ model })) {
        return false;
      } else if (cell.stencilType === "input") {
        return false;
      } else if (model.checkIsChild(cell)) {
        return true;
      } else if (cell.checkInPortIsAllConnected()) {
        return false;
      } else {
        return true;
      }
    });

    cells.map((cell) => {
      if (connectedCellsObj[cell.id]) {
        if (cell.outputType === "boolean") {
          const outPorts = cell.getGroupPorts("out");
          const items = [
            {
              cellId: cell.id,
              portId: outPorts[0].id,
              name: `${cell.attr("elementName/text")} 输入1`,
            },
            {
              cellId: cell.id,
              portId: outPorts[1].id,
              name: `${cell.attr("elementName/text")} 输入2`,
            },
          ];
          tmp.push(...items);
        } else {
          const item = {
            cellId: cell.id,
            portId: connectedCellsObj[cell.id].portId,
            name: cell.attr("elementName/text"),
          };
          tmp.push(item);
        }
      } else {
        if (cell.outputType === "boolean") {
          const inPorts = cell.getGroupPorts("in");
          const items = [
            {
              cellId: cell.id,
              portId: inPorts[0].id,
              name: `${cell.attr("elementName/text")} 输入1`,
            },
            {
              cellId: cell.id,
              portId: inPorts[1].id,
              name: `${cell.attr("elementName/text")} 输入2`,
            },
          ];
          tmp.push(...items);
        } else {
          const unUsedInPorts = cell.getUnusedInPorts();
          if (unUsedInPorts.length) {
            const item = {
              cellId: cell.id,
              portId: unUsedInPorts[0],
              name: cell.attr("elementName/text"),
            };
            tmp.push(item);
          }
        }
      }
    });

    return tmp;
  };

  const setData = (key, value) => {
    state.data[key] = value;
    model.setDataCopy(state.data);
  };

  const state = reactive({ data: R.clone(data), elementName });

  const propertiesFormListener = ref({});

  const setElementName = (text) => {
    state.elementName = text;
  };

  const addInPort = () => {
    model.addInPort();
  };

  const changeInPortConnect = (connectPortInfo, inPortInfoItem) => {
    return model.changeInPortConnect(connectPortInfo, inPortInfoItem);
  };

  const changeOutPortConnect = (connectPortInfo, outPortInfoItem) => {
    return model.changeOutPortConnect(connectPortInfo, outPortInfoItem);
  };

  const swapInPortsLink = (inPortInfoItem, conncetedInPortInfoItem) => {
    return model.swapInPortsLink(inPortInfoItem, conncetedInPortInfoItem);
  };

  const swapOutPortsLink = (outPortInfoItem, conncetedOutPortInfoItem) => {
    return model.swapOutPortsLink(outPortInfoItem, conncetedOutPortInfoItem);
  };

  const addPropertiesFormListener = (listener) => {
    const propertiesFormListenerClone = R.clone(propertiesFormListener.value);
    const key = nanoid();
    propertiesFormListenerClone[key] = listener;
    propertiesFormListener.value = propertiesFormListenerClone;
    return key;
  };

  const removePropertiesFormListener = (key) => {
    const propertiesFormListenerClone = R.clone(propertiesFormListener.value);
    delete propertiesFormListenerClone[key];
    propertiesFormListener.value = propertiesFormListenerClone;
  };

  const removeAllPropertiesFormListener = () => {
    propertiesFormListener.value = {};
  };

  const activatePropertiesFormListener = () => {
    for (let keyAndlistener of Object.entries(propertiesFormListener.value)) {
      keyAndlistener[1]();
    }
  };

  watch(
    () => state.elementName,
    (newValue) => {
      model.setElementName(newValue);
    }
  );

  return {
    state,
    methods: {
      setElementName,
      getInData,
      getOutData,
      addInPort,
      setData,
      getCanConnectInPortsList,
      getCanConnectOutPortsList,
      changeInPortConnect,
      changeOutPortConnect,
      createInData,
      createOutData,
      swapInPortsLink,
      swapOutPortsLink,
      removeInLink,
      removeOutLink,
      removeInPort,
      addPropertiesFormListener,
      activatePropertiesFormListener,
      removePropertiesFormListener,
      removeAllPropertiesFormListener,
    },
  };
}

export const CommonRectangle = joint.dia.Element.define(
  "stencil.commonRectangle",
  {
    attrs: {
      root: {
        magnet: false,
      },
      body: {
        refWidth: "100%",
        refHeight: "100%",
        strokeWidth: STROKE_WIDTH,
        stroke: "rgba(41, 54, 70, 1)",
        fill: "#010016",
      },
      labelGroup: {},
      labelGroupImage: {},
      labelGroupText: {
        textVerticalAnchor: "middle",
        // textAnchor: "middle",
        fontSize: 16,
        fontFamily: "Microsoft YaHei",
        fill: "rgba(204, 211, 217, 1)",
        fontWeight: "400",
      },
      elementName: {
        fontSize: 14,
        fontFamily: "Microsoft YaHei",
        fill: "rgba(204, 211, 217, 1)",
        fontWeight: "400",
      },
      result: {
        fontSize: 12,
        fontFamily: "Microsoft YaHei",
        fill: "rgba(204, 211, 217, 1)",
        fontWeight: "500",
      },
    },
    ports: {
      items: [],
      groups: {
        in: {
          position: { name: "left" },
          attrs: {
            portBody: {
              magnet: "passive",
              r: PORT_RADIUS,
              // cx: -Math.abs(PORT_RADIUS - 2),
              fill: "#010016",
              stroke: "rgba(15, 159, 100, 1)",
              strokeWidth: STROKE_WIDTH,
            },
          },
          z: 2,
        },
        out: {
          position: { name: "right" },
          attrs: {
            portBody: {
              magnet: "active",
              r: PORT_RADIUS,
              // cx: Math.abs(PORT_RADIUS - 2),
              fill: "#010016",
              stroke: "#ff3818",
              strokeWidth: STROKE_WIDTH,
            },
          },
          z: 2,
        },
      },
    },
    markup: [
      {
        tagName: "rect",
        selector: "body",
      },
      {
        tagName: "svg",
        selector: "labelGroup",
        children: [
          {
            tagName: "image",
            selector: "labelGroupImage",
            groupSelector: "labelGroupChildren",
          },
          {
            tagName: "text",
            selector: "labelGroupText",
            groupSelector: "labelGroupChildren",
          },
        ],
      },
      {
        tagName: "text",
        selector: "label",
      },
      {
        tagName: "text",
        selector: "elementName",
      },
      {
        tagName: "text",
        selector: "result",
      },
    ],
    portMarkup: [{ tagName: "circle", selector: "portBody" }],
  },
  {
    initSize: {
      width: 0,
      height: 0,
    },

    maxOutLinkNumber: 0,
    operator: "",
    stencilName: "",
    outputType: "",
    elementData: null,
    stencilElementKey: "",
    stencilType: "",

    labelGroupImageAttrs: {},

    getLabel() {
      return this.attributes.attrs.label.text;
    },

    getElementsByStencilType(stencilType) {
      const elements = this.graph.getElements();

      return elements.filter((element) => {
        return element.getStencilType() === stencilType;
      });
    },

    initStencilElementKey() {
      const stencilType = this.getStencilType();
      const sameStencilTypeElements =
        this.getElementsByStencilType(stencilType);

      const checkHasStencilElementKey = (key) => {
        let result = false;
        for (let i = 0; i < sameStencilTypeElements.length; i++) {
          const sameStencilTypeElement = sameStencilTypeElements[i];

          if (sameStencilTypeElement.stencilElementKey === key) {
            result = true;
            break;
          }
        }
        return result;
      };

      const getInitKey = (count) => {
        const initKey = `${stencilType}${count}`;
        const hasStencilElementKey = checkHasStencilElementKey(initKey);
        if (hasStencilElementKey) {
          return getInitKey(count + 1);
        } else return initKey;
      };

      const key = getInitKey(sameStencilTypeElements.length);
      this.stencilElementKey = key;
      this.attr("stencilElementKey", { key });
    },

    initElementName() {
      const sameStencilTypeElements = this.getElementsByStencilType(
        this.getStencilType()
      );

      const checkHasSameElementName = (name) => {
        let result = false;
        for (let i = 0; i < sameStencilTypeElements.length; i++) {
          const sameStencilTypeElement = sameStencilTypeElements[i];

          if (
            sameStencilTypeElement.attributes.attrs.elementName.text === name
          ) {
            result = true;
            break;
          }
        }
        return result;
      };

      const getInitName = (count) => {
        const initName = `${this.stencilName}${count}`;
        const hasSameElementName = checkHasSameElementName(initName);
        if (hasSameElementName) {
          return getInitName(count + 1);
        } else return initName;
      };

      const name = getInitName(sameStencilTypeElements.length);
      this.setElementName(name);
    },

    setElementName(text) {
      this.attr("elementName/text", text);
    },

    getElementName() {
      return this.attributes.attrs.elementName.text;
    },

    setLabel(text) {
      const newLabelTextWidth = textWidth({
        text,
        fontSize: 33,
        fontFamily: "Segoe UI",
        fontWeight: "800",
      });
      const cellSize = this.getSize();

      this.setSize(
        newLabelTextWidth + LABEL_PADDING_LEFT_RIGHT * 2,
        cellSize.height
      );

      this.attr("label/text", text);
    },

    setResult(result) {
      this.attr("result/text", result);
    },

    getSize() {
      const size = this.attributes.size;
      return {
        width: size.width + STROKE_WIDTH + PORT_RADIUS,
        height: size.height + STROKE_WIDTH + PORT_RADIUS,
      };
    },

    setSize(width, height) {
      this.size(
        // width + STROKE_WIDTH + PORT_RADIUS,
        // height + STROKE_WIDTH + PORT_RADIUS
        width,
        height
      );
    },

    getPrevDataCopy() {
      const prevData = this._previousAttributes.attrs.data || {};
      return R.clone(prevData);
    },

    getDataCopy() {
      const data = this.attributes.attrs.data || {};
      return R.clone(data);
    },

    setDataCopy(data, needWatch = true) {
      this.attr("data", data);
      if (needWatch) this.watcher(this.getPrevDataCopy(), this.getDataCopy());
    },

    initData() {
      const data = this.getDataCopy();
      this.elementData = elementData(data, this);
    },

    watch: {
      // constantValue: function (prevData, newData) {
      //   const newConstantValue = newData.constantValue;
      //   if (newData.inputType === "constant") {
      //     if (!R.isEmpty(newConstantValue)) {
      //       this.setLabel(newConstantValue);
      //     }
      //   }
      // },
    },

    watcher(prevData, newData) {
      for (let [key] of Object.entries(newData)) {
        if (!R.equals(prevData[key], newData[key])) {
          if (R.is(Function, this.watch[key])) {
            this.watch[key].call(this, prevData, newData);
          }
        }
      }
    },

    listenerHook: {
      dblclick() {
        // console.log("Ports", this.getPorts());
        console.log("this", this);
        console.log(this.graph.getConnectedLinks(this));
      },
    },

    listenerHooker(key, cellView, evt, x, y) {
      for (let [listenerHookKey] of Object.entries(this.listenerHook)) {
        if (listenerHookKey === key) {
          this.listenerHook[key].call(this, cellView, evt, x, y);
          break;
        }
      }
    },

    getGroupPorts(group) {
      return this.getPorts().filter(function (port) {
        return port.group === group;
      });
    },

    getStencilType() {
      return this.stencilType || this.attributes.type.replace(/^stencil\./, "");
    },

    checkIsMatchMaxOutLinkNumber() {
      let result = false;
      const maxOutLinkNumber = this.maxOutLinkNumber;
      const connectedLinks = this.graph.getConnectedLinks(this, {
        outbound: true,
      });
      if (maxOutLinkNumber === -1) {
        result = false;
      } else if (connectedLinks.length === maxOutLinkNumber) {
        result = true;
      }
      return result;
    },

    validateMagnet(cellView, magnet) {
      let result = true;
      const model = cellView.model;

      validate: {
        const portGroup = magnet.attributes.getNamedItem("port-group").value;
        if (portGroup === "in") {
          result = false;
          break validate;
        }
        const isMatchMaxOutLinkNumber = model.checkIsMatchMaxOutLinkNumber();
        if (isMatchMaxOutLinkNumber) {
          result = false;
          break validate;
        }
      }

      return result;
    },

    validateConnection(cellViewS, magnetS, cellViewT, magnetT) {
      let result = true;

      validate: {
        const sourcePortGroup =
          magnetS.attributes.getNamedItem("port-group").value;
        const targetPortGroup =
          magnetT.attributes.getNamedItem("port-group").value;
        if (sourcePortGroup !== "out" || targetPortGroup !== "in") {
          result = false;
          break validate;
        }

        const isConnectedSource = this.checkIsConnectedSource(cellViewS);
        if (isConnectedSource) {
          result = false;
          break validate;
        }

        const isTargetPortUsed = this.checkPortIsUsed(magnetT);
        if (isTargetPortUsed) {
          result = false;
          break validate;
        }

        const isDownstreamCell = this.checkIsDownstreamCell(cellViewS);
        if (isDownstreamCell) {
          result = false;
          break validate;
        }
      }

      return result;
    },

    checkIsDownstreamCell(cellViewS) {
      let result = false;
      const graph = this.graph;
      const modelS = cellViewS.model;
      const connectedLinks = graph.getConnectedLinks(modelS, {
        inbound: true,
      });

      const traverse = (links) => {
        for (let i = 0; i < links.length; i++) {
          const link = links[i];
          const sourceId = link.source().id;
          const target = graph.getCell(sourceId);
          if (target.id === this.id) {
            result = true;
            break;
          } else {
            const targetConnectedLinks = graph.getConnectedLinks(target, {
              inbound: true,
            });
            if (targetConnectedLinks.length) {
              traverse(targetConnectedLinks);
              if (result) break;
            }
          }
        }
      };

      traverse(connectedLinks);

      return result;
    },

    checkIsChild(childCellView) {
      let result = false;
      const cell = this;
      const graph = cell.graph;
      const connectedLinks = graph.getConnectedLinks(cell, {
        outbound: true,
      });

      for (let i = 0; i < connectedLinks.length; i++) {
        const link = connectedLinks[i];
        const target = link.target();
        if (target.id === childCellView.id) {
          result = true;
          break;
        }
      }

      return result;
    },

    checkIsParent(parentCellView) {
      let result = false;
      const cell = this;
      const graph = cell.graph;
      const connectedLinks = graph.getConnectedLinks(cell, { inbound: true });

      for (let i = 0; i < connectedLinks.length; i++) {
        const link = connectedLinks[i];
        const source = link.source();
        if (source.id === parentCellView.id) {
          result = true;
          break;
        }
      }

      return result;
    },

    checkIsConnectedSource(cellViewS) {
      let result = false;
      let count = 0;

      const graph = this.graph;
      const modelS = cellViewS.model;
      const connectedLinks = graph.getConnectedLinks(modelS, {
        outbound: true,
      });

      for (let i = 0; i < connectedLinks.length; i++) {
        const link = connectedLinks[i];
        const targetId = link.target().id;
        if (targetId === this.id) {
          count += 1;
          if (count === 1) {
            result = true;
            break;
          }
        }
      }

      return result;
    },

    checkInPortIsAllConnected() {
      const cell = this;
      const graph = cell.graph;

      const inPorts = cell.getGroupPorts("in");
      const inLinks = graph.getConnectedLinks(this, { inbound: true });

      let result = false;

      if (inLinks.length >= inPorts.length) {
        result = true;
      }

      return result;
    },

    getUnusedInPorts() {
      const cell = this;
      const graph = cell.graph;
      const result = [];

      const inPorts = cell.getGroupPorts("in");
      const inLinks = graph.getConnectedLinks(this, { inbound: true });
      inPorts.map((inPort) => {
        let tmp = false;
        for (let i = 0; i < inLinks.length; i++) {
          const inLink = inLinks[i];
          const target = inLink.target();
          if (target.port === inPort.id) {
            tmp = true;
            break;
          }
        }
        if (!tmp) result.push(inPort.id);
      });

      return result;
    },

    checkPortIsUsed(port) {
      const graph = this.graph;
      const portGroup = port.attributes.getNamedItem("port-group").value;
      let result = false;

      if (portGroup === "in") {
        const portId = port.attributes.getNamedItem("port").value;
        const connectedLinks = graph.getConnectedLinks(this);

        for (let i = 0; i < connectedLinks.length; i++) {
          const link = connectedLinks[i];
          if (link.target().port === portId) {
            result = true;
            break;
          }
        }
      } else if (portGroup === "out") {
        const portId = port.attributes.getNamedItem("port").value;
        const connectedLinks = graph.getConnectedLinks(this);

        for (let i = 0; i < connectedLinks.length; i++) {
          const link = connectedLinks[i];
          if (link.source().port === portId) {
            result = true;
            break;
          }
        }
      }

      return result;
    },

    checkPortIsUsedById(portId) {
      const graph = this.graph;
      const port = this.getPort(portId);
      const portGroup = port.group;
      let result = false;

      if (portGroup === "in") {
        const connectedLinks = graph.getConnectedLinks(this);

        for (let i = 0; i < connectedLinks.length; i++) {
          const link = connectedLinks[i];
          if (link.target().port === portId) {
            result = true;
            break;
          }
        }
      } else if (portGroup === "out") {
        const connectedLinks = graph.getConnectedLinks(this);

        for (let i = 0; i < connectedLinks.length; i++) {
          const link = connectedLinks[i];
          if (link.source().port === portId) {
            result = true;
            break;
          }
        }
      }

      return result;
    },

    checkOutPortIsUsed() {
      const graph = this.graph;
      const connectedLinks = graph.getConnectedLinks(this, { outbound: true });

      return Boolean(connectedLinks.length > 0);
    },

    checkInPortHasUsed() {
      const graph = this.graph;
      const connectedLinks = graph.getConnectedLinks(this, { inbound: true });

      return Boolean(connectedLinks.length > 0);
    },

    checkPortGroupIsOverflowCellHeight() {
      const outPorts = this.getGroupPorts("out");
      const inPorts = this.getGroupPorts("in");
      const maxPortsNum = Math.max(outPorts.length, inPorts.length);
      const initSize = this.initSize;
      const portsTotalHeight = maxPortsNum * PORT_RADIUS * 2;
      const tmpHeight = portsTotalHeight + 10;
      const cellSize = this.getSize();

      if (initSize.height >= tmpHeight) {
        this.setSize(cellSize.width, initSize.height);
      } else {
        this.setSize(cellSize.width, tmpHeight);
      }
    },

    /**
     * 设置输入输出口
     * @param {portsConfig} portsConfig
     */
    setPorts(portsConfig) {
      const inPorts = R.repeat({ group: "in" }, portsConfig.in);
      const outPorts = R.repeat({ group: "out" }, portsConfig.out);
      const ports = R.concat(inPorts, outPorts);
      this.addPorts(ports);
    },

    addInPort() {
      this.addPort({ group: "in" });
      this.resetCellSize();
    },

    addOutPort() {
      this.addPort({ group: "out" });
      this.resetCellSize();
    },

    removeInPort() {
      console.log(this);
    },

    resetCellSize() {
      this.resetCellWidth();
      this.resetCellHeight();
    },
    resetCellWidth() {
      const cell = this;
      const labelGroupImageAttrs = cell.attr("labelGroupImage");
      const labelGroupTextAttrs = cell.attr("labelGroupText");
      const cellSizeClone = R.clone(cell.size());

      if (labelGroupImageAttrs["xlink:href"] && labelGroupTextAttrs["text"]) {
        const labelGroupTextWidth = textWidth({
          text: labelGroupTextAttrs["text"],
          fontSize: Number(labelGroupTextAttrs.fontSize),
          fontFamily: labelGroupTextAttrs.fontFamily,
          fontWeight: 400,
        });

        const tmpLabelGroupTextAttrs = {
          ...labelGroupTextAttrs,
          x: labelGroupImageAttrs.width + 10,
          y: (cellSizeClone.height - labelGroupTextAttrs.fontSize / 2) / 2,
        };

        const labelGroupAttrs = {
          width: labelGroupTextWidth + tmpLabelGroupTextAttrs.x,
          height: labelGroupImageAttrs.height,
          x: PORT_RADIUS * 2,
          y: (cellSizeClone.height - labelGroupImageAttrs.height) / 2,
        };

        cell.attr("labelGroupText", tmpLabelGroupTextAttrs);
        cell.attr("labelGroup", labelGroupAttrs);
        cell.size(
          labelGroupAttrs.width + labelGroupAttrs.x * 2,
          cellSizeClone.height
        );
      } else if (labelGroupImageAttrs["xlink:href"]) {
        const labelGroupAttrs = {
          width: labelGroupImageAttrs.width,
          height: labelGroupImageAttrs.height,
          x: PORT_RADIUS * 2,
          y: (cellSizeClone.height - labelGroupImageAttrs.height) / 2,
        };

        cell.attr("labelGroup", labelGroupAttrs);
        cell.size(
          labelGroupAttrs.width + labelGroupAttrs.x * 2,
          cellSizeClone.height
        );
      }
    },
    resetCellHeight() {
      const cell = this;

      const cellSizeClone = R.clone(cell.size());

      const outPorts = cell.getGroupPorts("out");
      const inPorts = cell.getGroupPorts("in");
      const maxPortsNum = Math.max(outPorts.length, inPorts.length);
      const portsTotalHeight = maxPortsNum * PORT_RADIUS * 2;

      const labelGroupAttrs = cell.attr("labelGroup");
      const elementNameAttrs = cell.attr("elementName");
      const resultAttrs = cell.attr("result");

      if (portsTotalHeight >= labelGroupAttrs.height + 8) {
        cell.setSize(cellSizeClone.width, portsTotalHeight);
        cell.attr(
          "labelGroup/y",
          (portsTotalHeight - labelGroupAttrs.height) / 2
        );
        cell.attr(
          "elementName/y",
          portsTotalHeight + elementNameAttrs.fontSize
        );
        cell.attr(
          "result/y",
          portsTotalHeight +
            elementNameAttrs.fontSize +
            10 +
            resultAttrs.fontSize
        );
      } else {
        cell.setSize(cellSizeClone.width, labelGroupAttrs.height + 8);
        cell.attr("labelGroup/y", 4);
        cell.attr(
          "elementName/y",
          labelGroupAttrs.height + 8 + elementNameAttrs.fontSize + 5
        );
        cell.attr(
          "result/y",
          labelGroupAttrs.height +
            8 +
            elementNameAttrs.fontSize +
            10 +
            resultAttrs.fontSize +
            5
        );
      }
    },
    changeInPortConnect(connectPortInfo, inPortInfoItem) {
      const cell = this;
      const graph = cell.graph;
      if (!inPortInfoItem.linkId) {
        const link = new ConnectLink();
        link.addTo(graph);
        link.source({
          id: connectPortInfo.cellId,
          port: connectPortInfo.portId,
        });
        link.target({
          id: inPortInfoItem.targetCellId,
          port: inPortInfoItem.targetPortId,
        });
        return link.id;
      } else {
        const oldLink = graph.getCell(inPortInfoItem.linkId);
        oldLink.remove();
        const link = new ConnectLink();
        link.addTo(graph);
        link.source({
          id: connectPortInfo.cellId,
          port: connectPortInfo.portId,
        });
        link.target({
          id: inPortInfoItem.targetCellId,
          port: inPortInfoItem.targetPortId,
        });
        return link.id;
      }
    },
    changeOutPortConnect(connectPortInfo, outPortInfoItem) {
      const cell = this;
      const graph = cell.graph;
      if (!outPortInfoItem.linkId) {
        const link = new ConnectLink();
        link.addTo(graph);
        link.source({
          id: outPortInfoItem.sourceCellId,
          port: outPortInfoItem.sourcePortId,
        });
        link.target({
          id: connectPortInfo.cellId,
          port: connectPortInfo.portId,
        });
        return link.id;
      } else {
        const oldLink = graph.getCell(outPortInfoItem.linkId);
        oldLink.remove();
        const link = new ConnectLink();
        link.addTo(graph);
        link.source({
          id: outPortInfoItem.sourceCellId,
          port: outPortInfoItem.sourcePortId,
        });
        link.target({
          id: connectPortInfo.cellId,
          port: connectPortInfo.portId,
        });
        return link.id;
      }
    },
    swapInPortsLink(inPortInfoItem, conncetedInPortInfoItem) {
      const cell = this;
      const graph = cell.graph;

      if (!inPortInfoItem.linkId) {
        const oldConnectedPortLink = graph.getCell(
          conncetedInPortInfoItem.linkId
        );
        // console.log({
        //   type: "source",
        //   id: conncetedInPortInfoItem.sourceCellId,
        //   port: conncetedInPortInfoItem.sourcePortId,
        // });
        // console.log({
        //   type: "target",
        //   id: inPortInfoItem.targetCellId,
        //   port: inPortInfoItem.targetPortId,
        // });
        oldConnectedPortLink.source({
          id: conncetedInPortInfoItem.sourceCellId,
          port: conncetedInPortInfoItem.sourcePortId,
        });
        oldConnectedPortLink.target({
          id: inPortInfoItem.targetCellId,
          port: inPortInfoItem.targetPortId,
        });
      } else {
        const oldLink = graph.getCell(inPortInfoItem.linkId);
        const oldConnectedPortLink = graph.getCell(
          conncetedInPortInfoItem.linkId
        );

        oldLink.source({
          id: conncetedInPortInfoItem.sourceCellId,
          port: conncetedInPortInfoItem.sourcePortId,
        });
        oldLink.target({
          id: conncetedInPortInfoItem.targetCellId,
          port: conncetedInPortInfoItem.targetPortId,
        });

        oldConnectedPortLink.source({
          id: inPortInfoItem.sourceCellId,
          port: inPortInfoItem.sourcePortId,
        });
        oldConnectedPortLink.target({
          id: inPortInfoItem.targetCellId,
          port: inPortInfoItem.targetPortId,
        });
      }
    },
    swapOutPortsLink(outPortInfoItem, conncetedOutPortInfoItem) {
      const cell = this;
      const graph = cell.graph;

      if (!outPortInfoItem.linkId) {
        const oldConnectedPortLink = graph.getCell(
          conncetedOutPortInfoItem.linkId
        );
        oldConnectedPortLink.source({
          id: outPortInfoItem.sourceCellId,
          port: outPortInfoItem.sourcePortId,
        });
        oldConnectedPortLink.target({
          id: conncetedOutPortInfoItem.targetCellId,
          port: conncetedOutPortInfoItem.targetPortId,
        });
      } else {
        const oldLink = graph.getCell(outPortInfoItem.linkId);
        const oldConnectedPortLink = graph.getCell(
          conncetedOutPortInfoItem.linkId
        );

        oldLink.source({
          id: conncetedOutPortInfoItem.sourceCellId,
          port: conncetedOutPortInfoItem.sourcePortId,
        });
        oldLink.target({
          id: conncetedOutPortInfoItem.targetCellId,
          port: conncetedOutPortInfoItem.targetPortId,
        });

        oldConnectedPortLink.source({
          id: outPortInfoItem.sourceCellId,
          port: outPortInfoItem.sourcePortId,
        });
        oldConnectedPortLink.target({
          id: outPortInfoItem.targetCellId,
          port: outPortInfoItem.targetPortId,
        });
      }
    },
    activatePropertiesFormListener() {
      this.elementData.methods.activatePropertiesFormListener();
    },
  },
  {
    /**
     * 创建CommonRectangle
     * @param {CommonRectangleCreateConfig} config
     * @returns
     */
    create(config) {
      const cell = new this();
      cell.attr("data", config.data || {});
      cell.size(config.size.width, config.size.height);

      cell.setPorts(config.ports);
      cell.maxOutLinkNumber = config.ports.maxOutLinkNumber;

      // const cellSize = cell.size();
      // this.initSize = R.clone(cellSize);

      if (cell.labelGroupImageAttrs["xlink:href"]) {
        cell.attr("labelGroupImage", cell.labelGroupImageAttrs);
      }

      if (R.is(Object, cell.dataWatch)) {
        for (let [key, value] of Object.entries(cell.dataWatch)) {
          if (R.is(Function, value)) cell.watch[key] = value;
        }
      }

      cell.resetCellSize();

      return cell;
    },
    reloadCell(config) {
      const { cellJson, ports, initData } = config;
      const cell = new this();

      const formatData = (() => {
        const cellJsonData = cellJson.attrs.data;
        const tmp = {};
        for (let keyValue of Object.entries(initData)) {
          const key = keyValue[0];
          if (!R.isNil(cellJsonData[key])) {
            tmp[key] = cellJsonData[key];
          }
        }
        return tmp;
      })();

      cell.attr("data", formatData || {});
      cell.size(cellJson.size.width, cellJson.size.height);
      cell.position(cellJson.position.x, cellJson.position.y);
      cell.attr("elementName/text", cellJson.attrs.elementName.text || "");

      cell.stencilElementKey = cellJson.attrs.stencilElementKey.key;

      const portsNum = (() => {
        let inPortsNum = 0;
        let outPortsNum = 0;

        if (
          cellJson.ports &&
          Array.isArray(cellJson.ports.items) &&
          cellJson.ports.items.length
        ) {
          cellJson.ports.items.map((item) => {
            if (item.group === "in") inPortsNum += 1;
            else if (item.group === "out") outPortsNum += 1;
          });
        }

        return {
          in: inPortsNum,
          out: outPortsNum,
        };
      })();

      cell.setPorts(portsNum);

      cell.maxOutLinkNumber = ports.maxOutLinkNumber;

      if (cell.labelGroupImageAttrs["xlink:href"]) {
        cell.attr("labelGroupImage", cell.labelGroupImageAttrs);
      }

      if (R.is(Object, cell.dataWatch)) {
        for (let [key, value] of Object.entries(cell.dataWatch)) {
          if (R.is(Function, value)) cell.watch[key] = value;
        }
      }

      cell.resetCellSize();

      return cell;
    },
  }
);
